import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Translate } from 'react-redux-i18n'
import { HandleClickOutside } from '../HandleClickOutside'
import { Dropdown, positionDropdown } from '../dropdown'
import { calculateDropdownPosition } from '../dropdown/helpers'
import { Element } from '../html/element'
import { CalculateTooltipPosition } from './CalcPosition'
import { SIZE_ARROW } from './constants'
import {
  getArrowPositionStyle,
  getPositionInsideContent,
  getTooltipArrowClass,
} from './helpers'
import classes from './index.module.scss'

export const Tooltip = ({
  children,
  tooltipContent,
  isI18n,
  translateProps,
  position,
  customPosition,
  zIndex,
  tooltipSpace,
  contentClass,
  contentStyle,
  tooltipStyle,
  tooltipContentStyle,
  isInsideContent,
  isHiddenInsideContent,
  elementName: Component = 'div',
  disabled,
  styleDropdown,
  eventType,
  isDelayMouseOut,
}) => {
  const contentRef = useRef()
  const dropdownRef = useRef()

  // Use state
  const [isShow, setIsShow] = useState(false)
  const [cloneDropdownRect, setCloneDropdownRect] = useState()
  const [priorityPosition, setPriorityPosition] = useState()

  const timeout = useRef()

  // Actions
  const onMouseEnter = () => {
    if (disabled) return
    if (timeout.current) {
      clearTimeout(timeout.current)
    }
    setIsShow(true)
  }

  const onMouseLeave = () => {
    if (disabled) return
    if (isDelayMouseOut) {
      timeout.current = setTimeout(() => setIsShow(false), 150)
    } else {
      setIsShow(false)
    }
  }

  // Use effect
  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current)
      }
    }
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      if (dropdownRef.current?.getBoundingClientRect()) {
        const { width, height } = dropdownRef.current?.getBoundingClientRect()
        const dropdownRect = calculateDropdownPosition(
          contentRef,
          dropdownRef,
          position,
          (tooltipSpace || 0) + SIZE_ARROW,
        )
        const newDropdownRect = {
          width: width,
          height: height,
          left: dropdownRect.left || dropdownRect.right - width,
          right: dropdownRect.right || dropdownRect.left + width,
          top: dropdownRect.top || dropdownRect.bottom - height,
          bottom: dropdownRect.bottom || dropdownRect.top + height,
        }
        setCloneDropdownRect(newDropdownRect)
        dropdownRef.current.style.display = 'none'
        clearInterval(interval)
      }
      return () => clearInterval(interval)
    }, 100)
  }, [])

  useEffect(() => {
    let timeout
    const onScroll = (event) => {
      timeout = setTimeout(() => {
        const divRect = contentRef?.current?.getBoundingClientRect()
        if (
          !(
            event.clientX >= divRect?.left &&
            event.clientX <= divRect?.right &&
            event.clientY >= divRect?.top &&
            event.clientY <= divRect?.bottom
          )
        ) {
          onMouseLeave()
        }
        clearTimeout(timeout)
      }, [100])
    }
    window.addEventListener('wheel', onScroll)
    return () => {
      window.removeEventListener('wheel', onScroll)
      clearTimeout(timeout)
    }
  }, [])

  return (
    <>
      <Component
        ref={contentRef}
        style={contentStyle}
        className={[classes.content, contentClass].join(' ')}
        onMouseOver={onMouseEnter}
        onMouseOut={() =>
          isHiddenInsideContent || isDelayMouseOut ? onMouseLeave() : undefined
        }
      >
        {children}
      </Component>

      <div className="positionRelative">
        <Element
          ref={dropdownRef}
          className={classes.tooltipContentClone}
          style={{ fontSize: 12, ...tooltipContentStyle }}
        >
          {isI18n ? (
            <Translate value={tooltipContent} {...translateProps} />
          ) : (
            tooltipContent
          )}
        </Element>
      </div>

      {isShow && (
        <Dropdown
          parentRef={contentRef}
          position={priorityPosition || position}
          zIndex={zIndex}
          dropdownSpace={
            (tooltipSpace || 0) +
            SIZE_ARROW +
            (isInsideContent
              ? getPositionInsideContent(
                  contentRef,
                  priorityPosition || position,
                )
              : 0)
          }
          customPosition={customPosition}
          appendStyle={tooltipStyle}
          isTooltip
          styleDropdown={styleDropdown}
        >
          <HandleClickOutside
            eventType={eventType}
            exceptRef={contentRef}
            handleClickOutside={onMouseLeave}
          >
            <div className="positionRelative">
              <Element
                className={classes.tooltipContent}
                style={{
                  fontSize: 12,
                  color: '#FFFFFF',
                  backgroundColor: '#000000',
                  opacity: 0.7,
                  ...tooltipContentStyle,
                }}
                onMouseEnter={isDelayMouseOut ? onMouseEnter : undefined}
                onMouseLeave={isDelayMouseOut ? onMouseLeave : undefined}
              >
                {isI18n ? (
                  <Translate value={tooltipContent} {...translateProps} />
                ) : (
                  tooltipContent
                )}
              </Element>
              <div
                className={[
                  classes.tooltipArrow,
                  getTooltipArrowClass(priorityPosition || position),
                ].join(' ')}
                style={{
                  ...getArrowPositionStyle(
                    contentRef,
                    priorityPosition || position,
                  ),
                }}
              />
            </div>
          </HandleClickOutside>
        </Dropdown>
      )}

      <CalculateTooltipPosition
        contentRef={contentRef}
        dropdownRect={cloneDropdownRect}
        position={position}
        dropdownSpace={(tooltipSpace || 0) + SIZE_ARROW}
        setPriorityPosition={setPriorityPosition}
      />
    </>
  )
}

Tooltip.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  tooltipContent: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isI18n: PropTypes.bool,
  translateProps: PropTypes.object,
  position: PropTypes.string,
  customPosition: PropTypes.func,
  zIndex: PropTypes.number,
  tooltipSpace: PropTypes.number,
  contentClass: PropTypes.string,
  contentStyle: PropTypes.object,
  tooltipStyle: PropTypes.object,
  tooltipContentStyle: PropTypes.object,
  isInsideContent: PropTypes.bool,
  isHiddenInsideContent: PropTypes.bool,
  elementName: PropTypes.string,
  disabled: PropTypes.bool,
  styleDropdown: PropTypes.object,
  eventType: PropTypes.string,
  isDelayMouseOut: PropTypes.bool,
}

Tooltip.defaultProps = {
  position: positionDropdown.TOP_CENTER,
  zIndex: 10,
  tooltipSpace: 0,
  isI18n: false,
  translateProps: {},
  contentClass: '',
  contentStyle: {},
  tooltipStyle: {},
  tooltipContentStyle: {},
  isInsideContent: false,
  isHiddenInsideContent: false,
  disabled: false,
  styleDropdown: {},
  eventType: 'mousemove',
  isDelayMouseOut: false,
}
